import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  fetchSupportMessages,
  resolveSupportMessageAttachment,
  sortSupportMessages,
  type SupportMessage,
  type SupportMessageLike,
  upsertSupportMessage,
} from '@/lib/supportChat';

interface UseSupportMessagesOptions {
  conversationId?: string | null;
  enabled?: boolean;
}

export function useSupportMessages(options: UseSupportMessagesOptions = {}) {
  const { conversationId, enabled = true } = options;
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(Boolean(enabled && conversationId));
  const [error, setError] = useState<Error | null>(null);

  const refreshMessages = useCallback(async () => {
    if (!enabled || !conversationId) {
      setMessages([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const nextMessages = await fetchSupportMessages(conversationId);
      setMessages(nextMessages);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error('Failed to load support messages.'));
    } finally {
      setLoading(false);
    }
  }, [conversationId, enabled]);

  useEffect(() => {
    if (!enabled || !conversationId) {
      setMessages([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const loadMessages = async () => {
      setLoading(true);
      setError(null);

      try {
        const nextMessages = await fetchSupportMessages(conversationId);
        if (!cancelled) {
          setMessages(nextMessages);
        }
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError instanceof Error ? nextError : new Error('Failed to load support messages.'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadMessages();

    const channel = supabase
      .channel(`support-messages:${conversationId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `conversation_id=eq.${conversationId}` }, async (payload) => {
        const nextMessage = await resolveSupportMessageAttachment(payload.new as SupportMessage);
        setMessages((current) => upsertSupportMessage(current, nextMessage));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'support_messages', filter: `conversation_id=eq.${conversationId}` }, async (payload) => {
        const nextMessage = await resolveSupportMessageAttachment(payload.new as SupportMessage);
        setMessages((current) => upsertSupportMessage(current, nextMessage));
      })
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [conversationId, enabled]);

  const upsertMessage = (message: SupportMessageLike) => {
    setMessages((current) => upsertSupportMessage(current, message as SupportMessage));
  };

  const replaceMessages = (nextMessages: SupportMessageLike[]) => {
    setMessages(sortSupportMessages(nextMessages as SupportMessage[]));
  };

  return {
    messages,
    loading,
    error,
    upsertMessage,
    replaceMessages,
    refreshMessages,
  };
}
